import { autorun, reaction } from "mobx";
import { ApplicationStore } from "../../../../store/ApplicationStore";
import { CompositionStateStore } from "../../../../store/CompositionStateStore";
import { TimelineStore } from "../../../../store/TimelineStore";
import { createHighPPICanvas } from "../../../../utils/canvas";
import { drawCanvasTimelineRuler } from "./utils/canvasRuler";
import { useEffect, useRef, useState } from "react";
import './CanvasTimeRuler.styles.scss';

const CanvasTimeRuler = () => {
    const [duration, setDuration] = useState(CompositionStateStore.duration);
    const [timelineZoom, setTimelineZoom] = useState(TimelineStore.zoom);
    const [baseStartSec, setBaseStartSec] = useState(TimelineStore.baseStartSec);
    const [scrollLeft, setScrollLeft] = useState(0);
    // Fixed visual left gutter for timeline interactions.
    const leftPadPx = TimelineStore.leftPadPx;

    const canvasContainerRef = useRef(null);
    const canvasRef = useRef(null);
    const canvasContextRef = useRef(null);
    const canvasFillColorRef = useRef(null);
    const latestDrawOptionsRef = useRef({ duration, timelineZoom, baseStartSec, scrollLeft, leftPadPx });
    latestDrawOptionsRef.current = { duration, timelineZoom, baseStartSec, scrollLeft, leftPadPx };

    useEffect(() => {
        const disposeAutorun = autorun(() => {
            setDuration(CompositionStateStore.duration);
            setTimelineZoom(TimelineStore.zoom);
            setBaseStartSec(TimelineStore.baseStartSec);
        });

        const disposeReaction = reaction(
            () => ApplicationStore.theme,
            updateTheme
        );

        window.addEventListener('resize', resizeCanvas);

        return () => {
            disposeAutorun();
            disposeReaction();
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;

        if (canvasRef.current) {
            // @ts-ignore
            canvasContextRef.current = canvasRef.current.getContext('2d');
            // @ts-ignore
            canvasFillColorRef.current = ApplicationStore.theme === "dark" ? "#A7B0BC" : "#6B7280";
            resizeCanvas();

            if (canvasContainerRef.current && typeof ResizeObserver !== 'undefined') {
                resizeObserver = new ResizeObserver(resizeCanvas);
                resizeObserver.observe(canvasContainerRef.current);
            }
        }

        return () => resizeObserver?.disconnect();
    }, []);

    useEffect(() => {
        // @ts-ignore
        const handleTracksScrollLeft = (event) => {
            setScrollLeft(event.detail);
        };

        window.addEventListener('tracksScrollLeft', handleTracksScrollLeft);

        return () => {
            window.removeEventListener('tracksScrollLeft', handleTracksScrollLeft);
        };
    }, []);

    useEffect(() => {
        redrawCanvas();
    }, [duration, scrollLeft, timelineZoom, baseStartSec]);

    // @ts-ignore
    const handleMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!canvasContainerRef.current) {
            return;
        }

        // @ts-ignore
        const { left: leftOffset } = canvasContainerRef.current.getBoundingClientRect();
        const x = event.clientX - leftOffset + scrollLeft;
        const xAdjusted = x - leftPadPx;

        // Emit a custom event instead of using @Event decorator
        const customEvent = new CustomEvent('seekingTimeRulerStart', {
            bubbles: true,
            detail: { event, x: xAdjusted },
        });
        // @ts-ignore
        canvasContainerRef.current.dispatchEvent(customEvent);
    };

    const updateTheme = () => {
        // @ts-ignore
        canvasFillColorRef.current = ApplicationStore.theme === "dark" ? "#A7B0BC" : "#6B7280";
        redrawCanvas();
    };

    const resizeCanvas = () => {
        if (!canvasContainerRef.current || !canvasContextRef.current) {
            return;
        }

        // @ts-ignore
        const height = canvasContainerRef.current.clientHeight;
        // @ts-ignore
        const width = canvasContainerRef.current.clientWidth;

        createHighPPICanvas(canvasContextRef.current, width, height);
        redrawCanvas();
    };

    const redrawCanvas = () => {
        if (canvasContextRef.current) {
            const latest = latestDrawOptionsRef.current;
            drawCanvasTimelineRuler(canvasContextRef.current, latest.duration, {
                scrollOffset: latest.scrollLeft,
                fillColor: canvasFillColorRef.current ?? '#000',
                zoom: latest.timelineZoom,
                leftPadPx: latest.leftPadPx,
                baseStartSec: latest.baseStartSec,
                variant: "dotted",
            });
        }
    };

    return (
        <div
            className="canvas-time-ruler"
            onMouseDown={handleMouseDown}
            ref={canvasContainerRef}
        >
            <canvas ref={canvasRef}></canvas>
        </div>
    );
};

export default CanvasTimeRuler;
